'use client';

import type { FC, ReactNode, RefObject } from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from '../icons';
import { getPortalRoot } from '../portalRoot';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Centered header title (first line). */
  title: ReactNode;
  /** Optional centered second line under the title. */
  subtitle?: ReactNode;
  /** Right-side "Done" check button. When omitted the slot is a spacer
   *  so the centered title stays centered. */
  onDone?: () => void;
  doneAriaLabel?: string;
  closeAriaLabel?: string;
  /** Rendered below the header row, pinned (shrink-0) — e.g. filters. */
  headerAccessory?: ReactNode;
  /** Attached to the scrollable body so infinite-scroll hooks can bind. */
  scrollRef?: RefObject<HTMLDivElement | null>;
  bodyClassName?: string;
  /** Extra classes on the sheet panel — e.g. a background-image glow that
   *  sits behind the whole sheet (header + body) seamlessly. */
  panelClassName?: string;
  /** Drop the header row (title/Done) and float just a close button in the
   *  top-right corner — content then runs full-bleed to the top. */
  hideHeader?: boolean;
  /** Panel height. Default 90vh. */
  heightClass?: string;
  children: ReactNode;
}

/**
 * iOS 26 style modal bottom sheet.
 *
 *   ┌──── drag handle ────┐
 *   │ (X)     Title    (✓)│   X close left · centered title · optional Done right
 *   │      subtitle        │
 *   ├──── headerAccessory ─┤   (optional, pinned)
 *   ├──────────────────────┤
 *   │   scrollable body    │
 *   └──────────────────────┘
 *
 * Pinned with `fixed` + z-50 so it covers any sticky bottom bar on the
 * parent screen. Backdrop is a blurred scrim; tapping it closes.
 */
export const BottomSheet: FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  onDone,
  doneAriaLabel,
  closeAriaLabel,
  headerAccessory,
  scrollRef,
  bodyClassName,
  panelClassName,
  hideHeader = false,
  heightClass = 'h-[90vh]',
  children,
}) => {
  // Two-phase lifecycle so we can run a slide-down/fade-out animation on
  // close before unmounting. `isMounted` controls DOM presence;
  // `isExiting` toggles to the reverse-animation classes.
  const [isMounted, setIsMounted] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      setIsExiting(false);
      return;
    }
    if (!isMounted) return;
    setIsExiting(true);
    const timer = window.setTimeout(() => setIsMounted(false), 340);
    return () => window.clearTimeout(timer);
  }, [isOpen, isMounted]);

  // Lock body scroll while the sheet is visible.
  useEffect(() => {
    if (!isMounted) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isMounted]);

  if (!isMounted) return null;

  // Portal to <body> so the sheet escapes any ancestor stacking context
  // (TMA screen stack uses transforms — without this the sheet renders
  // behind the fixed tab bar / bottom buttons on iOS).
  return createPortal(
    <div className="fixed inset-0 z-[1100]">
      <div
        className={`absolute inset-0 bg-black/45 backdrop-blur-md ${isExiting ? 'animate-shot-sheet-fade-out' : 'animate-shot-sheet-fade-in'}`}
        onClick={onClose}
      />
      <div className={`absolute left-0 right-0 bottom-0 max-w-[480px] mx-auto rounded-t-[36px] bg-bg-secondary shadow-[0_-12px_40px_rgba(0,0,0,0.55)] flex flex-col ${heightClass} ${panelClassName ?? ''} ${isExiting ? 'animate-shot-sheet-slide-down' : 'animate-shot-sheet-slide-up'}`}>
        {hideHeader ? (
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-fill-tertiary text-label-secondary active:opacity-70 transition-opacity"
            aria-label={closeAriaLabel}
          >
            <FiX size={16} />
          </button>
        ) : (
          <div className="grid grid-cols-[44px_1fr_44px] items-center px-4 pt-4 pb-3 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-full flex items-center justify-center bg-fill-tertiary text-label-secondary active:opacity-70 transition-opacity justify-self-start"
              aria-label={closeAriaLabel}
            >
              <FiX size={16} />
            </button>
            <div className="flex flex-col items-center min-w-0">
              <div className="text-label-primary text-[15px] font-semibold text-center truncate tabular-nums">
                {title}
              </div>
              {subtitle != null && (
                <div className="text-label-primary text-[12px] font-semibold mt-0.5 tabular-nums">
                  {subtitle}
                </div>
              )}
            </div>
            {onDone ? (
              <button
                type="button"
                onClick={onDone}
                className="w-9 h-9 rounded-full bg-accent-blue text-label-primary flex items-center justify-center active:opacity-85 transition-opacity justify-self-end"
                aria-label={doneAriaLabel}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                  <path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            ) : (
              <span aria-hidden className="justify-self-end" />
            )}
          </div>
        )}
        {headerAccessory != null && (
          <div className="shrink-0 px-4 pb-2">{headerAccessory}</div>
        )}
        <div
          ref={scrollRef}
          className={`flex-1 overflow-y-auto px-4 pb-[max(20px,var(--tg-viewport-safe-area-inset-bottom,0px))] ${bodyClassName ?? ''}`}
        >
          {children}
        </div>
      </div>
    </div>,
    getPortalRoot(),
  );
};
